import { CaseBodyPart } from "../CaseBodyPart/caseBodyPart.model";

import {
  serializable,
  alias,
  object,
  list,
  primitive,
  custom,
  raw,
} from "serializr";
import { ScanCenterBranch } from "../ScanCenterBranch/scanCenterBranch.model";
import { Attachment } from "../Attachment/attachment.model";
import { BodyPartTypeEnum } from "../../enums/BodyPartType.enum";
import { Tag } from "../Tag/tag.model";
import { Report } from "../Report/report.model";

export class CaseDetail {
  @serializable(alias("id", primitive()))
  id?: number;

  @serializable(alias("patient_id", primitive()))
  patientId?: string;

  attachments: File[] = [];

  @serializable(alias("case_attachments", list(object(Attachment))))
  caseAttachments?: Attachment[] = [];

  @serializable(alias("patient_name", primitive()))
  patientName?: string;

  @serializable(alias("scan_center_branch", object(ScanCenterBranch)))
  scanCenterBranch?: ScanCenterBranch;

  @serializable(alias("scan_center_branch_id", primitive()))
  scanCenterBranchId?: string;

  @serializable(alias("assigned_to_doctor_name", primitive()))
  assignedToDoctorName?: string;

  @serializable(alias("approved_by_admin", primitive()))
  approvedByAdmin?: string;

  @serializable(alias("approved_by_admin_id", primitive()))
  approvedByAdminId?: number;

  @serializable(alias("scan_type_id", primitive()))
  scanTypeId?: number;

  @serializable(alias("due_date", primitive()))
  dueDate?: string;


  @serializable(alias("time_elapsed", primitive()))
  timeElapsed?: string;

  @serializable(alias("tat", primitive()))
  tat?: string;

  @serializable(alias("reported_time", primitive()))
  reportedTime?: string;

  @serializable(alias("scan_type_name", primitive()))
  scanTypeName?: string;

  @serializable(alias("created_at", primitive()))
  createdAt?: string;

  @serializable(alias("acquisition_date", primitive()))
  acquisitionDate?: string;

  @serializable(alias("scan_center_id", primitive()))
  scanCenterId?: number;

  @serializable(alias("scan_center_name", primitive()))
  scanCenterName?: string;

  @serializable(alias("view_access_ids", list(primitive())))
  viewAccessIds?: number[];

  @serializable(alias("tag_ids", list(primitive())))
  tagIds?: number[];

  @serializable(alias("tags", list(object(Tag))))
  tags?: Tag[] = [];

  @serializable(alias("age", primitive()))
  age?: string;

  @serializable(alias("gender", primitive()))
  gender?: string;

  @serializable(alias("current_clinical_problem", primitive()))
  currentClinicalProblem?: string;

  @serializable(alias("additional_details", primitive()))
  additionalDetails?: string;

  @serializable(alias("referring_doctor", primitive()))
  referringDoctor?: string;

  @serializable(alias("patient_history", primitive()))
  patientHistory?: string;

  @serializable(alias("contrast", primitive()))
  contrast?: string;

  @serializable(
    alias(
      "zip_file",
      custom(
        (file) => file,
        (json) => {}
      )
    )
  )
  zipFile?: File;

  @serializable(alias("status", primitive()))
  status?: string;

  @serializable(alias("reports", list(object(Report))))
  reports?: Report[] = [];

  @serializable(alias("lock", raw()))
  lock?: {
    locked: boolean;
    locked_by: {
      id: number;
      type: string;
      name: string;
      display_name: string;
    } | null;
    locked_at: string | null;
  } | null;

  @serializable(alias("case_status", primitive()))
  caseStatus?: string;

  @serializable(alias("upload_status", primitive()))
  uploadStatus?: string;

  selfAssign: boolean = false;

  @serializable(alias("assigned_to_doctor_id", primitive()))
  assignedToDoctorId?: string;

  @serializable(alias("assigned_by_doctor_id", primitive()))
  assignedByDoctorId?: string;

  @serializable(alias("assigned_to_admin_id", primitive()))
  assignedToAdminId?: string;

  @serializable(alias("assigned_to_admin_name", primitive()))
  assignedToAdminName?: string;

  @serializable(alias("study_description", primitive()))
  studyDescription?: string;

  /** Messages from others since last mark-read; listing badge (see CaseChatRead on API). */
  @serializable(alias("unread_chat_count", primitive()))
  unreadChatCount?: number;

  @serializable(alias("is_urgent", primitive()))
  isUrgent?: boolean;

  @serializable(alias("approval_required_for_case", primitive()))
  approvalRequiredForCase?: boolean;

  @serializable(alias("cosign_enabled", primitive()))
  cosignEnabled?: boolean;

  @serializable(alias("cosign_contributors", list(raw())))
  cosignContributors?: { id: number; name: string }[] = [];

  caseBodyPartIds: number[] = [];

  @serializable(alias("case_body_parts", list(object(CaseBodyPart))))
  caseBodyParts?: CaseBodyPart[] = [];
  static id: string | number | boolean | undefined;

  getFileName() {
    const bodyParts = this.caseBodyParts
      ?.map((caseBodyPart) => caseBodyPart?.bodyPartName)
      .join("_");
    return this.patientName + "_" + this.scanTypeName + "_" + bodyParts;
  }

  getStudyName() {
    let studyName = this.scanTypeName;
    const bodyPartString = this.caseBodyParts
      ? this.caseBodyParts
          .filter((part) => part.bodyPartName !== BodyPartTypeEnum.NOT_SET)
          .map((part) => part.bodyPartName)
          .join(",")
      : [].join("");
    const isStudyDescription =
      this.studyDescription && this.studyDescription.trim() !== "-";
    if (isStudyDescription && this.studyDescription) {
      studyName += " - " + this.studyDescription.trim();
    } else {
      if (bodyPartString && bodyPartString !== BodyPartTypeEnum.NOT_SET) {
        studyName += " - " + bodyPartString;
      }
    }
    return studyName;
  }

  public get parsedPatientHistory(): string {
    return !!this.patientHistory ? this.patientHistory.replace(/Test_history\s*/gi, "") : "";
  }

  public get parsedTagIds(): (number | undefined)[] {
    return this.tags?.map((tag) => tag.id) ?? [];
  }

}
